import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

function createStyles(theme) {
  return StyleSheet.create({
    headerWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
    },
    statusChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui?.border || 'rgba(255,255,255,0.12)',
      backgroundColor: theme.background?.card || 'rgba(255,255,255,0.04)',
      maxWidth: 120,
    },
    statusText: {
      fontSize: 10,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.35,
    },
    publishBtn: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
      backgroundColor: theme.brand?.primary || '#3ba378',
    },
    publishBtnDisabled: {
      opacity: 0.45,
    },
    publishBtnText: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.button?.primaryText || '#fff',
      textTransform: 'uppercase',
      letterSpacing: 0.35,
    },
    footerCard: {
      marginTop: 10,
      paddingTop: 10,
      paddingHorizontal: 10,
      paddingBottom: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui?.border || 'rgba(255,255,255,0.12)',
      backgroundColor: theme.background?.card || 'rgba(255,255,255,0.04)',
      minWidth: 0,
      gap: 8,
    },
    footerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
    },
    hint: {
      fontSize: 11,
      lineHeight: 15,
      color: theme.text?.secondary || '#888',
    },
  });
}

/**
 * Draft status + publish action for sidebar tab editors.
 * Header placement: compact chip + Publish pill beside expand/share.
 */
export function CalendarTabPublishBar({
  theme,
  styles: _legacyStyles,
  hasUnpublishedDraft,
  draftSaveState = 'idle',
  publishing = false,
  onPublish,
  placement = 'header',
  supportsApplyScope = false,
  applyScopePicker = null,
}) {
  const styles = useMemo(() => createStyles(theme), [theme]);

  const statusLabel =
    draftSaveState === 'saving'
      ? 'Saving…'
      : draftSaveState === 'error'
        ? 'Save failed'
        : hasUnpublishedDraft
          ? 'Draft'
          : 'Live';

  const statusColor =
    draftSaveState === 'error'
      ? theme.status?.error || '#e74c3c'
      : hasUnpublishedDraft
        ? theme.status?.warning || '#f59e0b'
        : theme.brand?.primary || '#3ba378';

  const statusIcon =
    draftSaveState === 'error'
      ? 'alert-circle-outline'
      : hasUnpublishedDraft
        ? 'document-text-outline'
        : 'checkmark-circle-outline';

  if (placement === 'header') {
    return (
      <View style={styles.headerWrap}>
        <View style={styles.statusChip}>
          <Ionicons name={statusIcon} size={11} color={statusColor} />
          <Text style={[styles.statusText, { color: statusColor }]} numberOfLines={1}>
            {statusLabel}
          </Text>
        </View>
        {hasUnpublishedDraft ? (
          <TouchableOpacity
            style={[styles.publishBtn, (publishing || draftSaveState === 'saving') && styles.publishBtnDisabled]}
            onPress={onPublish}
            disabled={publishing || draftSaveState === 'saving'}
          >
            {publishing ? (
              <ActivityIndicator size="small" color={theme.button?.primaryText || '#fff'} />
            ) : (
              <Text style={styles.publishBtnText}>Publish</Text>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.footerCard}>
      <View style={styles.footerRow}>
        <View style={styles.statusChip}>
          <Ionicons name={statusIcon} size={12} color={statusColor} />
          <Text style={[styles.statusText, { color: statusColor }]}>{statusLabel}</Text>
        </View>
        {hasUnpublishedDraft ? (
          <TouchableOpacity
            style={[styles.publishBtn, (publishing || draftSaveState === 'saving') && styles.publishBtnDisabled]}
            onPress={onPublish}
            disabled={publishing || draftSaveState === 'saving'}
          >
            {publishing ? (
              <ActivityIndicator size="small" color={theme.button?.primaryText || '#fff'} />
            ) : (
              <Text style={styles.publishBtnText}>Publish</Text>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
      {supportsApplyScope && hasUnpublishedDraft ? applyScopePicker : null}
      {hasUnpublishedDraft ? (
        <Text style={styles.hint}>
          Edits save automatically. Publish when you are ready to go live.
        </Text>
      ) : null}
    </View>
  );
}
